import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { getRegistration, submitRegistration, API_ORIGIN } from '../services/api';
import { jsPDF } from 'jspdf';

export default function RegistrationForm() {
  const { registrationId } = useParams<{ registrationId: string }>();
  const [reg, setReg] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<any>({});
  const [agreed, setAgreed] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    if (!registrationId) return;
    getRegistration(registrationId)
      .then(res => {
        setReg(res.data);
        const pf = res.data.prefillData || {};
        setFormData({
          firstName: pf.firstName || '',
          lastName: pf.lastName || '',
          nationalId: pf.nationalId || '',
          dateOfBirth: pf.dateOfBirth || '',
          gender: pf.gender || '',
          phone: pf.phone || '',
          email: pf.email || '',
          city: pf.city || '',
          zipCode: pf.zipCode || '',
          paymentMethod: '',
        });
        setLoading(false);
      })
      .catch(() => { setError('טופס הרישום לא נמצא'); setLoading(false); });
  }, [registrationId]);

  const getCanvasContext = () => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    return canvas.getContext('2d');
  };

  const getPos = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    if ('touches' in e) {
      return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
    }
    return { x: (e as React.MouseEvent).clientX - rect.left, y: (e as React.MouseEvent).clientY - rect.top };
  };

  const startDrawing = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    const ctx = getCanvasContext();
    if (!ctx) return;
    setIsDrawing(true);
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    if (!isDrawing) return;
    const ctx = getCanvasContext();
    if (!ctx) return;
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => setIsDrawing(false);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const validateStep1 = () => {
    const errors: string[] = [];
    if (!formData.firstName) errors.push('שם פרטי');
    if (!formData.lastName) errors.push('שם משפחה');
    if (!formData.nationalId || formData.nationalId.length !== 9) errors.push('ת.ז. (9 ספרות)');
    if (!formData.dateOfBirth) errors.push('תאריך לידה');
    if (!formData.gender) errors.push('מין');
    if (!formData.city) errors.push('יישוב');
    if (!formData.phone) errors.push('טלפון');
    if (!formData.email) errors.push('דוא"ל');
    if (!formData.paymentMethod) errors.push('אמצעי תשלום');
    setValidationErrors(errors);
    return errors.length === 0;
  };

  const goToStep2 = () => {
    if (validateStep1()) {
      setStep(2);
      setValidationErrors([]);
    }
  };

  const handleSubmit = async () => {
    if (!registrationId || !canvasRef.current) return;
    const signatureData = canvasRef.current.toDataURL('image/png');
    try {
      await submitRegistration(registrationId, {
        formData: { ...formData, agreed: true, signedDate: new Date().toISOString().split('T')[0] },
        signatureData,
      });
      setSubmitted(true);
    } catch {
      setError('שליחת הטופס נכשלה, אנא נסה שוב');
    }
  };

  const generatePDF = () => {
    if (!registrationId) return;
    // Download PDF from server (generated with pdfkit + Hebrew font support)
    window.open(`${API_ORIGIN}/api/registrations/${registrationId}/pdf`, '_blank');
  };

  const prefilledStyle = (value: string) => ({
    background: value ? '#FFFDE7' : '#fff',
  });

  const paymentMethodLabels: Record<string, string> = {
    credit_card: 'כרטיס אשראי',
    bank_transfer: 'העברה בנקאית',
    checks: 'המחאות',
    cash: 'מזומן',
  };

  const todayDate = new Date().toISOString().split('T')[0];

  const commitmentText = `מצהיר/ה ומתחייב/ת בזה כלפי מנארה מ.סי בע״מ (להלן: ״המוסד״) כדלקמן:
נרשמתי במוסד ללימודים מכינה קדם אקדמאית ו/או לימודים על תיכוניים בשנה״ל ${reg?.academicYear || ''}, וידוע לי כי עליי לשלם שכ״ל בגין לימודיי במוסד.
1. הנני מתחייב/ת לפרוע את שכר הלימוד במלואו בהתאם למועדי התשלומים הנקבעים על ידי המוסד ואם אאחר בפירעון אחד מן התשלומים על פי התאריכים הקבועים, תהיה למוסד הזכות לתבוע ריבית כחוק ולמנוע ממני לגשת לבחינות או לקבל כל שירות אחר מהמוסד.
2. במקרה שאפסיק או יופסקו לימודיי על ידי המוסד במהלך אותה שנה, אני מתחייב/ת לשלם את שכר הלימוד עבור לימודיי כמפורט להלן:
א. דמי ההרשמה לא יוחזרו באף מקרה.
ב. הופסקו לימודיי לאחר ההרשמה אך לפני תחילת הלימודים ביותר מ-14 ימים, לא אחויב בתשלום כלשהו משכר הלימוד השנתי מלבד דמי ההרשמה.
ג. הופסקו לימודיי לאחר ההרשמה אך לפני תחילת הלימודים בפחות מ-14 ימים, אחויב בתשלום 40% משכר הלימוד השנתי.
ד. הופסקו לימודיי במהלך החודש הראשון ללימודים אחויב בתשלום 80% משכר הלימוד השנתי.
ה. הופסקו לימודיי בחודש השני ללימודים או לאחר מכן אחויב בתשלום מלוא שכר הלימוד השנתי.
3. התאריך הקובע להפסקת הלימודים, הוא תאריך קבלת ההודעה בכתב של הסטודנט/ית על הפסקת לימודיו/ה למנהל התלמידים, וקבלת אישור נתקבל של המוסד על המכתב ההפסקה, ו/או תאריך קבלת הודעת הפסקת הלימודים, לאי-מייל של המוסד שהינו: haviva@aamc.co.il
4. הנני מאשר/ת שאין לי ולא יהיו לי טענות כלשהן נגד המוסד בעניין שכר הלימוד או דמי ההרשמה.
5. הנני להצהיר כי ידוע לי היטב כי המוסד רשאי לשנות את תכנית הלימודים במשך כל שנת הלימודים ובין היתר החלפת קורסים ותוכניות לימודים כפי שיראה המוסד לנכון מעת לעת. שינויים מסוג זה לא יפגעו בשכר הלימוד שנקבע.
6. ידוע לי שתכנית המכינה והתגבורים שמנהל המוסד הן תכניות פנים מוסדיות, ואין להן קשר לתוכניות של מוסדות אקדמיים אחרים שהמוסד משתף עמן פעולה.
7. הנני לאשר שתוכן ומטרות ומהות התכניות, התגבור הוסברו לי היטב על ידי צוות היועצים במוסד.
8. הנני מאשר/ת שהובא לידיעתי והוסבר לי את משמעות כתב התחייבות זה וכי חתימתי על כתב התחייבות זה הינה מרצוני הטוב והחופשי ומהווה הסכמה מלאה מטעמי על לימודיי במוסד והתחייבותי לתשלום שכר הלימוד באופן מלא ולעמוד בכל הדרישות של המוסד.`;

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5', direction: 'rtl', fontFamily: 'Heebo, Arial, sans-serif' }}>
      <p>טוען...</p>
    </div>
  );

  if (error && !reg) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5', direction: 'rtl', fontFamily: 'Heebo, Arial, sans-serif' }}>
      <div style={{ background: '#fff', padding: 40, borderRadius: 16, textAlign: 'center', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>&#x274C;</div>
        <h2>{error}</h2>
      </div>
    </div>
  );

  if (submitted || reg?.status === 'submitted') return (
    <div style={{ minHeight: '100vh', background: '#f5f5f5', direction: 'rtl', fontFamily: 'Heebo, Arial, sans-serif', padding: '40px 16px' }}>
      <div style={{ maxWidth: 650, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#7B1FA2' }}>&#x0645;&#x0646;&#x0627;&#x0631;&#x0629; | &#x05DE;&#x05DB;&#x05DC;&#x05DC;&#x05EA; &#x05DE;&#x05E0;&#x05D0;&#x05E8;&#x05D4;</div>
          <div style={{ height: 4, background: '#81D4FA', borderRadius: 2, marginTop: 8 }}></div>
        </div>
        <div style={{ background: '#fff', padding: 40, borderRadius: 16, textAlign: 'center', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
          <div style={{ width: 80, height: 80, borderRadius: '50%', background: '#4CAF50', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontSize: 40, color: '#fff' }}>&#x2713;</div>
          <h2 style={{ color: '#4CAF50', marginBottom: 8 }}>&#x05D4;&#x05D8;&#x05D5;&#x05E4;&#x05E1; &#x05E0;&#x05E9;&#x05DC;&#x05D7; &#x05D1;&#x05D4;&#x05E6;&#x05DC;&#x05D7;&#x05D4;! &#x05EA;&#x05D5;&#x05D3;&#x05D4; &#x05E2;&#x05DC; &#x05D4;&#x05E8;&#x05E9;&#x05DE;&#x05EA;&#x05DA; &#x05DC;&#x05DE;&#x05DB;&#x05DC;&#x05DC;&#x05EA; &#x05DE;&#x05E0;&#x05D0;&#x05E8;&#x05D4;</h2>
          <p style={{ color: '#666', fontSize: 16, marginBottom: 20 }}>&#x062A;&#x0645; &#x0625;&#x0631;&#x0633;&#x0627;&#x0644; &#x0627;&#x0644;&#x0646;&#x0645;&#x0648;&#x0630;&#x062C; &#x0628;&#x0646;&#x062C;&#x0627;&#x062D;! &#x0634;&#x0643;&#x0631;&#x0627;&#x064B; &#x0644;&#x062A;&#x0633;&#x062C;&#x064A;&#x0644;&#x0643; &#x0641;&#x064A; &#x0643;&#x0644;&#x064A;&#x0629; &#x0627;&#x0644;&#x0645;&#x0646;&#x0627;&#x0631;&#x0629;</p>
          <div style={{ background: '#f5f5f5', padding: 16, borderRadius: 8, textAlign: 'right', marginBottom: 20 }}>
            <div style={{ marginBottom: 6 }}><strong>&#x05E9;&#x05DD;:</strong> {formData.firstName} {formData.lastName}</div>
            <div style={{ marginBottom: 6 }}><strong>&#x05EA;.&#x05D6;.:</strong> {formData.nationalId}</div>
            <div style={{ marginBottom: 6 }}><strong>&#x05DE;&#x05E1;&#x05DC;&#x05D5;&#x05DC;:</strong> {reg?.courseName}</div>
            <div><strong>&#x05EA;&#x05D0;&#x05E8;&#x05D9;&#x05DA;:</strong> {todayDate}</div>
          </div>
          <button onClick={generatePDF} style={{
            padding: '14px 32px', borderRadius: 8, border: 'none',
            background: '#7B1FA2', color: '#fff', fontSize: 16, fontWeight: 700,
            cursor: 'pointer'
          }}>
            &#x05D4;&#x05D5;&#x05E8;&#x05D3; &#x05D4;&#x05E2;&#x05EA;&#x05E7; PDF
          </button>
        </div>
        <div style={{ textAlign: 'center', marginTop: 20, color: '#999', fontSize: 13 }}>
          &#x05D2;&#x05D1;&#x05E2;&#x05EA; &#x05D7;&#x05D1;&#x05D9;&#x05D1;&#x05D4; | 04-8853588 | &#x062C;&#x064A;&#x0641;&#x0639;&#x0627;&#x062A; &#x062D;&#x0628;&#x064A;&#x0628;&#x0647;
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#f5f5f5', direction: 'rtl', fontFamily: 'Heebo, Arial, sans-serif', padding: '30px 16px' }}>
      <div style={{ maxWidth: 700, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ fontSize: 26, fontWeight: 700, color: '#7B1FA2' }}>מכללת מנארה</div>
          <div style={{ height: 4, background: '#81D4FA', borderRadius: 2, marginTop: 8 }}></div>
        </div>

        {/* Step 1 - Student Details */}
        {step === 1 && (
          <div style={{ background: '#fff', padding: 28, borderRadius: 16, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
            <h2 style={{ color: '#7B1FA2', marginBottom: 4, fontSize: 20 }}>טופס רישום אלקטרוני - מכללת מנארה</h2>
            <p style={{ color: '#999', marginBottom: 20, fontSize: 14 }}>טופס רישום אלקטרוני</p>

            {/* Course info - readonly */}
            <div style={{ background: '#f3e5f5', padding: 14, borderRadius: 8, marginBottom: 20 }}>
              <div style={{ fontWeight: 700, fontSize: 16, color: '#7B1FA2' }}>{reg.courseName}</div>
              <div style={{ color: '#666', marginTop: 4 }}>שכר לימוד: <strong>{reg.tuitionAmount?.toLocaleString()} &#x20AA;</strong></div>
            </div>

            {/* Form fields */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 13 }}>שם פרטי *</label>
                <input style={{ ...inputStyle, ...prefilledStyle(reg.prefillData?.firstName) }} value={formData.firstName}
                  onChange={e => setFormData({ ...formData, firstName: e.target.value })} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 13 }}>שם משפחה *</label>
                <input style={{ ...inputStyle, ...prefilledStyle(reg.prefillData?.lastName) }} value={formData.lastName}
                  onChange={e => setFormData({ ...formData, lastName: e.target.value })} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 13 }}>ת.ז. (9 ספרות) *</label>
                <input style={{ ...inputStyle, direction: 'ltr', textAlign: 'left', ...prefilledStyle(reg.prefillData?.nationalId) }}
                  value={formData.nationalId} maxLength={9}
                  onChange={e => setFormData({ ...formData, nationalId: e.target.value.replace(/\D/g, '').slice(0, 9) })} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 13 }}>תאריך לידה *</label>
                <input type="date" style={{ ...inputStyle, ...prefilledStyle(reg.prefillData?.dateOfBirth) }} value={formData.dateOfBirth}
                  onChange={e => setFormData({ ...formData, dateOfBirth: e.target.value })} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 13 }}>מין *</label>
                <select style={{ ...inputStyle, ...prefilledStyle(reg.prefillData?.gender) }} value={formData.gender}
                  onChange={e => setFormData({ ...formData, gender: e.target.value })}>
                  <option value="">בחר...</option>
                  <option value="male">זכר</option>
                  <option value="female">נקבה</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 13 }}>יישוב *</label>
                <input style={{ ...inputStyle, ...prefilledStyle(reg.prefillData?.city) }} value={formData.city}
                  onChange={e => setFormData({ ...formData, city: e.target.value })} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 13 }}>מיקוד</label>
                <input style={{ ...inputStyle, direction: 'ltr', textAlign: 'left', ...prefilledStyle(reg.prefillData?.zipCode) }}
                  value={formData.zipCode} onChange={e => setFormData({ ...formData, zipCode: e.target.value })} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 13 }}>טלפון *</label>
                <input style={{ ...inputStyle, direction: 'ltr', textAlign: 'left', ...prefilledStyle(reg.prefillData?.phone) }}
                  value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 13 }}>דואר אלקטרוני *</label>
                <input type="email" dir="ltr" style={{ ...inputStyle, textAlign: 'left', ...prefilledStyle(reg.prefillData?.email) }}
                  value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, fontSize: 13 }}>אמצעי תשלום *</label>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  {Object.entries(paymentMethodLabels).map(([key, label]) => (
                    <label key={key} style={{
                      display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px',
                      borderRadius: 8, border: `2px solid ${formData.paymentMethod === key ? '#7B1FA2' : '#ddd'}`,
                      background: formData.paymentMethod === key ? '#f3e5f5' : '#fff', cursor: 'pointer', fontSize: 14
                    }}>
                      <input type="radio" name="paymentMethod" value={key} checked={formData.paymentMethod === key}
                        onChange={e => setFormData({ ...formData, paymentMethod: e.target.value })} style={{ display: 'none' }} />
                      {label}
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {validationErrors.length > 0 && (
              <div style={{ background: '#ffebee', color: '#c62828', padding: 12, borderRadius: 8, marginTop: 16, fontSize: 13 }}>
                יש למלא את השדות הבאים: {validationErrors.join(', ')}
              </div>
            )}

            {error && (
              <div style={{ background: '#ffebee', color: '#c62828', padding: 12, borderRadius: 8, marginTop: 16, fontSize: 13 }}>{error}</div>
            )}

            <button onClick={goToStep2} style={{
              width: '100%', padding: 14, borderRadius: 8, border: 'none',
              background: '#7B1FA2', color: '#fff', fontSize: 16, fontWeight: 700,
              cursor: 'pointer', marginTop: 20
            }}>
              המשך להצהרה &larr;
            </button>
          </div>
        )}

        {/* Step 2 - Declaration */}
        {step === 2 && (
          <div style={{ background: '#fff', padding: 28, borderRadius: 16, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
            <h2 style={{ color: '#7B1FA2', marginBottom: 16, fontSize: 20 }}>הצהרה וכתב התחייבות - אלקטרוני</h2>

            <div style={{ background: '#f3e5f5', padding: 12, borderRadius: 8, marginBottom: 16, fontSize: 14 }}>
              אני הח&quot;מ: <strong>{formData.firstName} {formData.lastName}</strong> ת.ז. <strong>{formData.nationalId}</strong>, מ-ישוב <strong>{formData.city}</strong> טל: <strong>{formData.phone}</strong>
            </div>

            {/* Commitment text */}
            <div style={{
              border: '1px solid #ddd', borderRadius: 8, padding: 16, marginBottom: 20,
              maxHeight: 300, overflowY: 'auto', fontSize: 13, lineHeight: 1.8, background: '#fafafa',
              whiteSpace: 'pre-wrap'
            }}>
              {commitmentText}
            </div>

            {/* Checkbox */}
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 20, cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>
              <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)}
                style={{ width: 20, height: 20, marginTop: 2, accentColor: '#7B1FA2' }} />
              קראתי והבנתי את כל תנאי כתב ההתחייבות ואני מסכים/ה
            </label>

            {/* Date - auto */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 20 }}>
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 13 }}>תאריך</label>
                <input style={{ ...inputStyle, background: '#e8e8e8' }} value={todayDate} readOnly />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, fontSize: 13 }}>שם מלא</label>
                <input style={{ ...inputStyle, background: '#e8e8e8' }} value={`${formData.firstName} ${formData.lastName}`} readOnly />
              </div>
            </div>

            {/* Signature Canvas */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, fontSize: 14 }}>חתום כאן:</label>
              <div style={{ border: '2px solid #7B1FA2', borderRadius: 8, background: '#fff', touchAction: 'none', position: 'relative' }}>
                <canvas
                  ref={canvasRef}
                  width={600}
                  height={150}
                  style={{ width: '100%', height: 150, cursor: 'crosshair', display: 'block' }}
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                />
                {!hasSignature && (
                  <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', color: '#ccc', fontSize: 16, pointerEvents: 'none' }}>
                    חתום כאן
                  </div>
                )}
              </div>
              <button onClick={clearCanvas} style={{ marginTop: 8, padding: '6px 16px', borderRadius: 6, border: '1px solid #ddd', background: '#f5f5f5', cursor: 'pointer', fontSize: 13 }}>
                נקה חתימה
              </button>
            </div>

            {error && (
              <div style={{ background: '#ffebee', color: '#c62828', padding: 12, borderRadius: 8, marginBottom: 16, fontSize: 13 }}>{error}</div>
            )}

            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={() => setStep(1)} style={{
                flex: 1, padding: 14, borderRadius: 8, border: '2px solid #7B1FA2',
                background: '#fff', color: '#7B1FA2', fontSize: 15, fontWeight: 600, cursor: 'pointer'
              }}>
                &rarr; חזור לפרטים
              </button>
              <button onClick={handleSubmit} disabled={!agreed || !hasSignature} style={{
                flex: 2, padding: 14, borderRadius: 8, border: 'none',
                background: '#7B1FA2', color: '#fff', fontSize: 16, fontWeight: 700,
                cursor: agreed && hasSignature ? 'pointer' : 'not-allowed',
                opacity: agreed && hasSignature ? 1 : 0.5
              }}>
                שלח טופס רישום
              </button>
            </div>
          </div>
        )}

        {/* Footer */}
        <div style={{ textAlign: 'center', marginTop: 20, color: '#999', fontSize: 13 }}>
          גבעת חביבה | 04-8853588
        </div>
      </div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 8,
  border: '1px solid #ddd',
  fontSize: 14,
  boxSizing: 'border-box',
  fontFamily: 'inherit',
};
